// game/zones.ts — the zone registry and the runner that sequences a zone.
//
// Zone shape (DESIGN §3): roomCount combat rooms -> Respite -> Boss -> descend.
// Phase 5 turns the Respite on. The Boss step is still gated by ZONE_FEATURES.boss
// and startBossRoom(); until a boss exists the runner falls through to the
// zone-complete reward, which is the seam boss work splices into.

import type { GeneratorDef, RoomTemplate, RunCtx, Vec, ZoneConfig, ZoneId, ZoneMechanic } from '../types'
import { registerEnemyDef, registerGeneratorDef, registerProjectile } from './entities'
import { registerRecipe } from '../engine/sprites'
import { hasQueryFlag, queryNumber, type Rng } from '../engine/rng'
import { makeBoss } from './boss'
import { makeGenerator } from './generators'
import { buildRoom } from './rooms'
import { barks } from './barks'
import type { Run } from './game'

export interface ZoneEntry {
  config: ZoneConfig
  mechanic: ZoneMechanic
}

const ZONES = new Map<number, ZoneEntry>()

export function registerZone(config: ZoneConfig, mechanic: ZoneMechanic): void {
  ZONES.set(config.id, { config, mechanic })
}

/** Data files call this to publish their enemy/projectile tables + sprite recipes. */
export function registerZoneContent(defs: {
  enemies?: import('../types').EnemyDef[]
  projectiles?: import('../types').ProjectileDef[]
  generators?: import('../types').GeneratorDef[]
}): void {
  if (defs.enemies) {
    for (const d of defs.enemies) {
      registerEnemyDef(d)
      registerRecipe(d.id, d.sprite)
    }
  }
  if (defs.projectiles) for (const p of defs.projectiles) registerProjectile(p)
  if (defs.generators) {
    for (const g of defs.generators) {
      registerGeneratorDef(g)
      registerRecipe(g.id, g.sprite)
    }
  }
}

export function getZone(id: number): ZoneEntry | undefined {
  return ZONES.get(id)
}

export function zoneIds(): number[] {
  return [...ZONES.keys()].sort((a, b) => a - b)
}

export type ZoneStep =
  | { kind: 'room'; template: RoomTemplate; index: number }
  | { kind: 'respite'; template: RoomTemplate }
  | { kind: 'boss'; bossId: string }
  | { kind: 'complete' }

export const ZONE_FEATURES = {
  respite: true,
  /**
   * The boss step. TEST SEAM `?noboss=1` turns it off so the run-flow specs can
   * keep asserting the pre-boss zone-clear payout path without fighting a boss
   * with a lawnmower policy. Never set during normal play.
   */
  boss: !hasQueryFlag('noboss'),
}

/**
 * TEST SEAM `?bossonly=1` — skip the combat rooms and open the zone on its
 * respite, one step from the boss door. Same simulation, no shortcut through the
 * fight itself: the Warden still has to walk into the stair and win.
 */
const BOSS_ONLY = hasQueryFlag('bossonly')

// ---------------------------------------------------------------- boss arenas
// A boss owns its arena the way a zone owns its rooms: content/bosses/bossN.ts
// registers the template from its zone's data file, and the runner builds it.
// Arenas are deliberately NOT in ZoneConfig.rooms — the combat draw must never
// be able to pick one.

const ARENAS = new Map<string, RoomTemplate>()

export function registerBossArena(bossId: string, template: RoomTemplate): void {
  ARENAS.set(bossId, template)
}

/**
 * The destructible parts of a boss (the Broodgate's spawn-maws) are generators:
 * every weapon, splash, burn trail and potion already knows how to chew them,
 * and Snapshot.generators counts them. Bosses build theirs through this seam so
 * content never has to reach past RunCtx into the run's entity arrays.
 */
export interface BossPart {
  readonly pos: Vec
  alive: boolean
  hp: number
  maxHp: number
  radius: number
  /** seconds to the next pour — drives the shared generator charge glow */
  timer: number
  spriteId: string
}

export function spawnBossPart(ctx: RunCtx, def: GeneratorDef, x: number, y: number): BossPart {
  const run = ctx as Run
  const g = makeGenerator(def, x, y, run.generators.length, 1)
  run.generators.push(g)
  return g
}

/**
 * The kill: the victory celebration first (fanfare, gold bursts, then the boss
 * and everything hostile it left on screen are removed — Run.bossVictory), and
 * at its end the zone-clear reward exactly as a zone completion pays it (embers,
 * gold, the free 1-of-3 relic), then the cut stair. Walking into it ends the
 * zone — see Run.advanceRoom, which is where zone 2 chains in.
 */
export function bossDefeated(ctx: RunCtx): void {
  const run = ctx as Run
  run.bossVictory()
}

/**
 * SEAM — build the boss arena and put the zone's boss in it.
 * Returns false when the boss has no registered arena/factory, which makes the
 * respite's boss door fall through to the plain zone-complete reward.
 */
export function startBossRoom(run: Run, bossId: string): boolean {
  const arena = ARENAS.get(bossId)
  if (!arena) return false
  const w = arena.map[0]?.length ?? 44
  const h = arena.map.length
  center.x = (w * 32) / 2
  center.y = (h * 32) / 2
  const boss = makeBoss(bossId, center, 1)
  if (!boss) return false

  buildRoom(run, arena, run.zoneRunner.order.length, false)
  // buildRoom runs the zone mechanic's per-room line, which is written for combat
  // rooms and is the wrong voice for the arena. The first thing said in here
  // belongs to the boss (its build() speaks on the first step).
  barks.clear()
  run.boss = boss
  run.isBossRoom = true
  run.lockDoors() // the stair is cut when the boss falls, not before
  return true
}

const center: Vec = { x: 0, y: 0 }

/** A room template is the zone's respite if it carries the altar glyph. */
export function isRespiteTemplate(t: RoomTemplate): boolean {
  for (const row of t.map) if (row.indexOf('A') >= 0) return true
  return false
}

/** A room template carries the timed vault if it has a 'V' door. */
export function hasVaultDoor(t: RoomTemplate): boolean {
  for (const row of t.map) if (row.indexOf('V') >= 0) return true
  return false
}

/**
 * Contiguous, as-even-as-possible split of `len` into `n` group sizes, remainder
 * pushed to the *later* groups so an extra template lands in the harder half.
 * (6,3) -> [2,2,2]; (8,3) -> [2,3,3].
 */
function groupSizes(len: number, n: number, out: number[]): number[] {
  out.length = 0
  const base = Math.floor(len / n)
  const rem = len - base * n
  for (let i = 0; i < n; i++) out.push(base + (i >= n - rem ? 1 : 0))
  return out
}

export class ZoneRunner {
  entry: ZoneEntry | null = null
  order: RoomTemplate[] = []
  cursor = 0
  /** index into `order` of the room carrying the timed vault, -1 if none. */
  vaultRoom = -1
  private respite: RoomTemplate | null = null
  private readonly sizes: number[] = []
  private stage: 'rooms' | 'respite' | 'boss' | 'done' = 'rooms'

  // --- spurs topology (D-021, zone 6): hub ↔ 3 one-room spurs → boss at 3 seals
  mode: 'linear' | 'spurs' = 'linear'
  spurs: RoomTemplate[] = []
  spurDone: boolean[] = []
  seals = 0
  /** which spur the Warden is in, -1 = hub */
  activeSpur = -1

  get config(): ZoneConfig | null {
    return this.entry?.config ?? null
  }

  get mechanic(): ZoneMechanic | null {
    return this.entry?.mechanic ?? null
  }

  get roomIndex(): number {
    return Math.max(0, this.cursor - 1)
  }

  get isBossStage(): boolean {
    return this.stage === 'boss'
  }

  start(zoneId: ZoneId, rng: Rng): boolean {
    const entry = getZone(zoneId)
    if (!entry) return false
    this.entry = entry
    this.cursor = 0
    this.stage = 'rooms'
    this.mode = entry.config.flow === 'spurs' ? 'spurs' : 'linear'
    this.seals = 0
    this.activeSpur = -1
    this.spurs.length = 0
    this.spurDone.length = 0
    this.order.length = 0
    this.vaultRoom = -1
    if (this.mode === 'spurs') {
      // The hub is the zone's respite template; spurs resolve from spurIds.
      this.respite = null
      for (const t of entry.config.rooms) {
        if (isRespiteTemplate(t) && !this.respite) this.respite = t
      }
      const ids = entry.config.spurIds ?? []
      for (const id of ids) {
        const t = entry.config.rooms.find((r) => r.id === id)
        if (t) {
          this.spurs.push(t)
          this.spurDone.push(false)
        }
      }
      // ?bossonly=1: pretend every seal is placed so the stair opens at once.
      if (BOSS_ONLY) {
        this.seals = this.spurs.length
        for (let i = 0; i < this.spurDone.length; i++) this.spurDone[i] = true
      }
      return this.respite != null && this.spurs.length > 0
    }
    this.pickRooms(entry.config, rng)
    return true
  }

  /** spurs: hub door `doorIndex` opens spur `doorIndex` (once). */
  spurStep(doorIndex: number): ZoneStep | null {
    if (this.mode !== 'spurs') return null
    if (doorIndex < 0 || doorIndex >= this.spurs.length) return null
    if (this.spurDone[doorIndex]) return null
    this.activeSpur = doorIndex
    return { kind: 'room', template: this.spurs[doorIndex], index: doorIndex }
  }

  /** spurs: leaving a cleared spur seals it and returns the hub template. */
  sealSpur(): RoomTemplate | null {
    if (this.mode !== 'spurs' || this.activeSpur < 0) return null
    if (!this.spurDone[this.activeSpur]) {
      this.spurDone[this.activeSpur] = true
      this.seals++
    }
    this.activeSpur = -1
    return this.respite
  }

  get hubTemplate(): RoomTemplate | null {
    return this.respite
  }

  /**
   * Rooms are picked so the teaching progression survives shuffling: the combat
   * library is cut into roomCount ordered groups and one room is drawn from each,
   * in order. Zone 1's library is authored (1 gen, 1 gen), (2 gen ×3),
   * (3 gen ×3) — so the run always reads easy -> choice -> gate.
   *
   * Then exactly one timed vault is guaranteed (DESIGN §3: "one per zone … a
   * golden side door in room 2 or 3"): the natural draw excludes vault templates,
   * and one eligible slot in rooms 2..N is swapped for a vault template from that
   * slot's own group, so the difficulty ramp is preserved.
   */
  private pickRooms(cfg: ZoneConfig, rng: Rng): void {
    this.order.length = 0
    this.vaultRoom = -1

    const combat: RoomTemplate[] = []
    this.respite = null
    for (const t of cfg.rooms) {
      if (isRespiteTemplate(t)) {
        if (!this.respite) this.respite = t
      } else {
        combat.push(t)
      }
    }
    if (combat.length === 0) return
    // ?bossonly=1: no combat rooms at all, so next() opens on the respite.
    if (BOSS_ONLY) return

    const want = queryNumber('rooms')
    const roomCount = want && want > 0 ? Math.min(6, Math.round(want)) : cfg.roomCount
    const n = Math.max(1, Math.min(roomCount, combat.length))
    groupSizes(combat.length, n, this.sizes)

    // one non-vault draw per group
    let lo = 0
    const bounds: { lo: number; hi: number }[] = []
    for (let i = 0; i < n; i++) {
      const hi = lo + this.sizes[i] - 1
      bounds.push({ lo, hi })
      let pick = -1
      for (let tries = 0; tries < 12; tries++) {
        const c = rng.int(lo, hi)
        if (!hasVaultDoor(combat[c])) {
          pick = c
          break
        }
      }
      if (pick < 0) {
        for (let c = lo; c <= hi; c++) {
          if (!hasVaultDoor(combat[c])) {
            pick = c
            break
          }
        }
      }
      if (pick < 0) pick = rng.int(lo, hi)
      this.order.push(combat[pick])
      lo = hi + 1
    }

    // the vault swap — rooms 2..N by design, room 1 only if that is all there is
    const slots: number[] = []
    for (let i = n > 1 ? 1 : 0; i < n; i++) {
      const b = bounds[i]
      for (let c = b.lo; c <= b.hi; c++) {
        if (hasVaultDoor(combat[c])) {
          slots.push(i)
          break
        }
      }
    }
    if (slots.length > 0) {
      const slot = slots.length === 1 ? slots[0] : slots[rng.int(0, slots.length - 1)]
      const b = bounds[slot]
      const vaults: number[] = []
      for (let c = b.lo; c <= b.hi; c++) if (hasVaultDoor(combat[c])) vaults.push(c)
      this.order[slot] = combat[vaults.length === 1 ? vaults[0] : vaults[rng.int(0, vaults.length - 1)]]
      this.vaultRoom = slot
    }
  }

  /** Advance the sequence and report what the game should build next. */
  next(): ZoneStep {
    // spurs mode: the only sequential step is the opening hub; everything after
    // is door-driven (spurStep/sealSpur) until the boss stair.
    if (this.mode === 'spurs') {
      if (this.stage === 'rooms') {
        this.stage = 'boss'
        if (this.respite) return { kind: 'respite', template: this.respite }
      }
      if (this.stage === 'boss') {
        this.stage = 'done'
        if (ZONE_FEATURES.boss && this.entry) return { kind: 'boss', bossId: this.entry.config.bossId }
      }
      this.stage = 'done'
      return { kind: 'complete' }
    }
    if (this.stage === 'rooms') {
      if (this.cursor < this.order.length) {
        const template = this.order[this.cursor]
        const index = this.cursor
        this.cursor++
        return { kind: 'room', template, index }
      }
      this.stage = ZONE_FEATURES.respite && this.respite ? 'respite' : 'boss'
    }
    if (this.stage === 'respite' && this.respite) {
      this.stage = 'boss'
      return { kind: 'respite', template: this.respite }
    }
    if (this.stage === 'boss') {
      this.stage = 'done'
      if (ZONE_FEATURES.boss && this.entry) {
        return { kind: 'boss', bossId: this.entry.config.bossId }
      }
    }
    this.stage = 'done'
    return { kind: 'complete' }
  }
}
